/**
 * v6.1.17.7 N.2 — Linked-accounts panel.
 *
 * Renders 3 rows on the Profile page:
 *   • Email (always linked — every user signed up with one)
 *   • Google (linked / not linked / coming soon)
 *   • Apple (linked / not linked / coming soon)
 *
 * Data source: GET /api/users/me/linked_methods + GET /api/auth/oauth/configured.
 *
 * "Coming soon" disabled state when the corresponding env var
 * (GOOGLE_OAUTH_CLIENT_ID / APPLE_BUNDLE_ID) is unset on the backend.
 *
 * Connect button initiates the OAuth flow.  For Web, this redirects to
 * `/auth/oauth/<provider>/callback` after the provider's consent screen
 * — see OAUTH_SETUP.md for the full cutover checklist.  Until provider
 * env vars are configured, the button is disabled.
 */
import { useEffect, useState } from 'react';
import api from '../lib/api';
import { authAPI } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { CheckCircle2, Circle, Mail, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const APPLE_SRC = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';

function loadScript(src) {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      if (existing.dataset.loaded) return resolve();
      existing.addEventListener('load', resolve);
      existing.addEventListener('error', reject);
      return undefined;
    }
    const s = document.createElement('script');
    s.src = src;
    s.async = true;
    s.onload = () => { s.dataset.loaded = '1'; resolve(); };
    s.onerror = reject;
    document.head.appendChild(s);
    return undefined;
  });
}

function PROVIDER_META() {
  return [
    { key: 'password', label: 'Email & password', icon: Mail, type: 'password' },
    { key: 'google', label: 'Google', icon: null, type: 'oauth' },
    { key: 'apple', label: 'Apple', icon: null, type: 'oauth' },
  ];
}

function ProviderIcon({ provider }) {
  if (provider === 'google') {
    return (
      <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
        <path fill="#FBBC05" d="M5.84 14.09a6.97 6.97 0 0 1 0-4.18V7.07H2.18a11 11 0 0 0 0 9.86l3.66-2.84z"/>
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
      </svg>
    );
  }
  if (provider === 'apple') {
    return (
      <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current" aria-hidden="true">
        <path d="M17.05 12.04c-.02-2.05 1.68-3.05 1.76-3.1-.96-1.4-2.45-1.59-2.99-1.6-1.27-.13-2.48.75-3.13.75-.65 0-1.65-.73-2.71-.71-1.39.02-2.67.81-3.39 2.04-1.45 2.51-.37 6.21 1.04 8.25.69.99 1.51 2.1 2.59 2.06 1.04-.04 1.43-.67 2.69-.67s1.61.67 2.71.65c1.12-.02 1.83-1.01 2.51-2 .79-1.15 1.12-2.26 1.14-2.32-.02-.01-2.18-.84-2.2-3.33zM15.07 5.31c.57-.7.96-1.66.85-2.62-.83.03-1.83.55-2.42 1.24-.52.61-.99 1.6-.86 2.54.92.07 1.86-.47 2.43-1.16z"/>
      </svg>
    );
  }
  return null;
}

function ConnectButton({ provider, configured, linked, cfg, onLinked }) {
  // Inline link-confirm state (an existing password account must confirm the
  // link with their password — mirrors OAuthCallback's link_required UI).
  const [linkToken, setLinkToken] = useState(null);
  const [linkPassword, setLinkPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const finishLink = async () => {
    if (!linkPassword || !linkToken) return;
    setBusy(true);
    try {
      await authAPI.oauthLinkConfirm({ confirmation_token: linkToken, password: linkPassword });
      toast.success('Apple linked!');
      setLinkToken(null);
      setLinkPassword('');
      onLinked?.();
    } catch (e) {
      const code = e.response?.data?.detail?.code;
      if (code === 'LINK_PASSWORD_INCORRECT') toast.error('Wrong password — try again.');
      else if (code === 'LINK_TOKEN_INVALID_OR_EXPIRED') {
        toast.error('Link request expired — please retry.');
        setLinkToken(null);
      } else toast.error('Could not link — try again.');
    } finally {
      setBusy(false);
    }
  };

  const handleConnectGoogle = () => {
    // Google id_token flow: full-page redirect, token returns in the URL
    // fragment (#id_token=…) which OAuthCallback parses. Works on a static
    // SPA host (no server POST needed). CSRF `state` stored in sessionStorage.
    const _rand = new Uint8Array(16);
    (window.crypto || window.msCrypto).getRandomValues(_rand);
    const _hex = Array.from(_rand, (b) => b.toString(16).padStart(2, '0')).join('');
    const stateToken = `google_${_hex}_${Date.now()}`;
    try { sessionStorage.setItem('fws_oauth_state_google', stateToken); } catch { /* ignore */ }
    const clientId = (process.env.REACT_APP_GOOGLE_CLIENT_ID || cfg?.google_client_id || '').trim();
    if (!clientId) {
      toast.error('Social login is not configured yet. Please try again later.');
      return;
    }
    const params = new URLSearchParams({
      client_id: clientId,
      redirect_uri: `${window.location.origin}/auth/oauth/google/callback`,
      response_type: 'id_token',
      scope: 'openid email profile',
      nonce: stateToken,
      state: stateToken,
    });
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params}`;
  };

  const handleConnectApple = async () => {
    // 2026-08-12 audit (GPT-5.2 + Opus 4.7 + Gemini P0): the previous full-page
    // `response_mode=form_post` redirect POSTed the credential to a CLIENT-SIDE
    // SPA route that can NEVER receive a POST body — Apple linking was 100%
    // broken. Use the Apple JS popup SDK (same working flow as the login page):
    // it returns the id_token IN JAVASCRIPT, which we POST via api. No redirect,
    // no form_post.
    const servicesId = cfg?.apple_services_id;
    if (!servicesId) {
      toast.error('Apple sign-in is not configured yet. Please try again later.');
      return;
    }
    setBusy(true);
    try {
      await loadScript(APPLE_SRC);
      window.AppleID.auth.init({
        clientId: servicesId,
        scope: 'name email',
        redirectURI: `${window.location.origin}/`,
        usePopup: true,
      });
      const res = await window.AppleID.auth.signIn();
      const resp = await api.post('/auth/oauth/apple', { id_token: res.authorization.id_token });
      if (resp.data?.link_required) {
        // Existing password account → confirm with password inline.
        setLinkToken(resp.data.confirmation_token);
      } else {
        toast.success('Apple linked!');
        onLinked?.();
      }
    } catch (err) {
      if (err?.error === 'popup_closed_by_user') { /* user cancelled — silent */ }
      else {
        const detail = err?.response?.data?.detail;
        toast.error((typeof detail === 'string' ? detail : detail?.message) || 'Apple sign-in failed.');
      }
    } finally {
      setBusy(false);
    }
  };

  if (linked) {
    return (
      <Badge variant="default" className="gap-1" data-testid={`linked-${provider}-status`}>
        <CheckCircle2 className="h-3 w-3" /> Linked
      </Badge>
    );
  }
  if (!configured) {
    return (
      <Badge variant="outline" className="opacity-60" data-testid={`linked-${provider}-disabled`}>
        Coming soon
      </Badge>
    );
  }
  if (linkToken) {
    return (
      <div className="flex items-center gap-1" data-testid={`linked-${provider}-confirm`}>
        <Input
          type="password"
          placeholder="Your password"
          value={linkPassword}
          onChange={(e) => setLinkPassword(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') finishLink(); }}
          disabled={busy}
          className="h-8 w-32"
          data-testid={`linked-${provider}-confirm-password`}
        />
        <Button size="sm" onClick={finishLink} disabled={!linkPassword || busy}
          data-testid={`linked-${provider}-confirm-submit`}>
          {busy ? '…' : 'Link'}
        </Button>
      </div>
    );
  }
  return (
    <Button
      variant="outline"
      size="sm"
      disabled={busy}
      onClick={provider === 'google' ? handleConnectGoogle : handleConnectApple}
      data-testid={`linked-${provider}-connect`}
    >
      {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Connect'}
    </Button>
  );
}

export default function LinkedAccountsPanel() {
  const [methods, setMethods] = useState(null);
  const [configured, setConfigured] = useState({ google: false, apple: false });
  const [loading, setLoading] = useState(true);

  const fetchState = async () => {
    try {
      const [m, c] = await Promise.all([
        api.get('/users/me/linked_methods'),
        api.get('/auth/oauth/configured'),
      ]);
      setMethods(m.data);
      setConfigured(c.data);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[linked-accounts] fetch failed:', e?.message);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await fetchState();
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div
      className="bg-card border border-border/60 rounded-2xl p-4 mb-4"
      data-testid="linked-accounts-panel"
    >
      <h3 className="text-sm font-semibold mb-3">Linked accounts</h3>
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : (
        <ul className="space-y-2">
          {PROVIDER_META().map((p) => {
            const linked = methods?.[p.key];
            const cfg = p.type === 'password' ? true : configured[p.key];
            return (
              <li
                key={p.key}
                className="flex items-center justify-between px-3 py-2 rounded-lg bg-secondary/20"
                data-testid={`linked-row-${p.key}`}
              >
                <div className="flex items-center gap-2">
                  {p.icon ? <p.icon className="h-4 w-4 text-muted-foreground" /> : <ProviderIcon provider={p.key} />}
                  <span className="text-sm font-medium">{p.label}</span>
                </div>
                {p.type === 'password' ? (
                  <Badge variant={linked ? 'default' : 'outline'}>
                    {linked ? <><CheckCircle2 className="h-3 w-3 mr-1" />Linked</> : <><Circle className="h-3 w-3 mr-1" />Not set</>}
                  </Badge>
                ) : (
                  <ConnectButton
                    provider={p.key}
                    configured={cfg}
                    linked={!!linked}
                    cfg={configured}
                    onLinked={fetchState}
                  />
                )}
              </li>
            );
          })}
        </ul>
      )}
      <p className="text-[10px] text-muted-foreground mt-3">
        Connecting a social login lets you sign in faster next time.  Unlinking is coming in a future update.
      </p>
    </div>
  );
}
